import { ReactNode, useState } from 'react';
import SharedSvg from './shared-svg';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { DisabledTooltipMenuItem } from './datagrids/tooltip-grid-actions-cell-item';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import MoreVert from '@mui/icons-material/MoreVert';

export interface SimpleDrawerAction {
  label: string;
  icon?: ReactNode;
  disabled: boolean;
  disabledReason?: string;
  onClick: () => void;
}
interface SimpleDrawerProps {
  title: string;
  children: ReactNode;
  width?: number | string;
  sx?: SxProps<Theme>;
  open: boolean;
  onClose?: () => void;
  isLoading?: boolean;
  actionsMenu?: SimpleDrawerAction[];
}

export default function SimpleDrawer({
  title,
  children,
  sx,
  open,
  onClose,
  isLoading,
  actionsMenu,
}: SimpleDrawerProps) {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleActionClick = (action: SimpleDrawerAction) => {
    handleMenuClose();
    action.onClick();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', md: '35%' },
          border: '1px solid',
          borderRadius: '4px',
          borderColor: theme.palette.semantic.outline.light,
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          ...sx,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            backgroundColor: theme.palette.semantic.surface.bg1,
            py: '10px',
            pl: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SharedSvg variant="red" width={16} height={16} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                color: theme.palette.semantic.text.title,
              }}
            >
              {title}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center">
            {actionsMenu && actionsMenu.length > 0 && (
              <>
                <Tooltip title="Actions">
                  <span>
                    <IconButton
                      onClick={handleMenuOpen}
                      aria-label="Drawer actions"
                    >
                      <MoreVert />
                    </IconButton>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  elevation={1}
                  onClose={handleMenuClose}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: theme.palette.semantic.surface.bg1,
                        border:
                          theme.palette.mode === 'dark'
                            ? `0.5px solid ${theme.palette.semantic.outline.light}`
                            : 'none',
                      },
                    },
                  }}
                >
                  {actionsMenu.map((action) => (
                    <DisabledTooltipMenuItem
                      key={action.label}
                      onClick={() => handleActionClick(action)}
                      disabled={action.disabled}
                      data-disabled-reason={action.disabledReason || undefined}
                    >
                      {action.icon && (
                        <ListItemIcon>{action.icon}</ListItemIcon>
                      )}
                      {action.label}
                    </DisabledTooltipMenuItem>
                  ))}
                </Menu>
              </>
            )}
            {onClose && (
              <IconButton onClick={onClose} aria-label="Close drawer">
                <Close />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Divider />
        <Box
          sx={{
            flexGrow: 1,
            height: 'calc(100% - 70px)',
            width: '100%',
            overflowY: 'auto',
            p: 2,
            pb: 0,
          }}
        >
          {isLoading ? (
            <Stack
              sx={{ width: '100%', height: '100%' }}
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress />
            </Stack>
          ) : (
            children
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
